import { config } from '../config';

export type IssueStatus = 'open' | 'in_progress' | 'resolved';
export type IssuePriority = 'low' | 'medium' | 'high';
export type AwaitingAction = 'staff' | 'customer' | 'none';
export type AccountStatus = 'approved' | 'pending_review' | 'disabled';
export type AccountTier = 'standard' | 'advanced';
export type ClaimStatus = 'primary' | 'contested' | 'invalid';
export type FlagReason =
  | 'duplicate_address_claim'
  | 'no_project_mapping'
  | 'invalid_address_id'
  | 'pending_too_long'
  | 'high_issue_volume';

export interface Issue {
  projectId: string;
  addressId: string;
  issueId: string;
  issueReferenceNumber?: string;
  issueNumber?: number;
  title: string;
  description: string;
  status: IssueStatus;
  priority: IssuePriority;
  awaitingAction: AwaitingAction;
  createdAt: string;
  updatedAt: string;
  ownerSub: string;
  ownerEmail?: string;
  attachments: Attachment[];
  // admin-only enriched fields
  ownerAddress?: string;
  projectReferenceNumber?: string;
  projectDisplayName?: string;
}

export interface Attachment {
  attachmentId: string;
  filename: string;
  contentType: string;
  size: number;
  s3Key: string;
  uploadedAt: string;
  uploadedBy: string;
}

export interface NotificationPreferences {
  emailOnStaffComment: boolean;
}

export interface Me {
  sub: string;
  email: string;
  accountStatus: AccountStatus;
  accountTier: AccountTier;
  flagged?: boolean;
  flagReasons: FlagReason[];
  addresses: MeAddress[];
  notificationPreferences: NotificationPreferences;
}
export interface MeAddress {
  addressId: string;
  claimStatus: ClaimStatus;
  streetAddress: string;
  suburb: string;
  state: string;
  postcode: string;
  projects: { projectId: string; displayName: string; projectReferenceNumber: string }[];
}

export interface AdminCustomerSummary {
  sub: string;
  email: string;
  accountStatus: AccountStatus;
  accountTier: AccountTier;
  flagged: boolean;
  flagReasons: FlagReason[];
  createdAt: string;
  addresses?: {
    addressId: string;
    streetAddress: string;
    suburb: string;
    state: string;
    postcode: string;
  }[];
}

export interface AdminAddress {
  addressId: string;
  streetAddress: string;
  suburb: string;
  state: string;
  postcode: string;
  createdAt: string;
  projectSummary: { projectId: string; projectReferenceNumber: string; displayName: string }[];
}

export interface AdminProject {
  pid: string;
  projectReferenceNumber: string;
  displayName: string;
  mainAddress: string;
  createdAt: string;
}

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public detail?: unknown,
  ) {
    super(message);
  }
}

export interface PublicAddress {
  addressId: string;
  streetAddress: string;
  suburb: string;
  state: string;
  postcode: string;
}

export function composeAddress(a: {
  streetAddress: string;
  suburb: string;
  state: string;
  postcode: string;
}): string {
  return `${a.streetAddress}, ${a.suburb} ${a.state} ${a.postcode}`;
}

async function raw(
  method: string,
  path: string,
  token: string | null,
  body?: unknown,
): Promise<unknown> {
  const res = await fetch(`${config.apiUrl}${path}`, {
    method,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: token } : {}),
    },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  const text = await res.text();
  const parsed = text ? safeJson(text) : null;
  if (!res.ok) {
    const message =
      (parsed && typeof parsed === 'object' && 'error' in parsed
        ? String((parsed as { error: unknown }).error)
        : '') || `HTTP ${res.status}`;
    throw new ApiError(res.status, message, parsed);
  }
  return parsed;
}

function safeJson(s: string): unknown {
  try {
    return JSON.parse(s);
  } catch {
    return s;
  }
}

export interface Comment {
  commentId: string;
  issueId: string;
  body: string;
  authorSub: string;
  authorEmail: string;
  authorDisplayName?: string;
  authorRole: string;
  createdAt: string;
  editedAt?: string;
  attachments: Attachment[];
}

export type AuditEventType =
  | 'issue_created'
  | 'status_changed'
  | 'priority_changed'
  | 'title_changed'
  | 'description_changed'
  | 'comment_added'
  | 'attachment_added';

export interface AuditEvent {
  issueId: string;
  eventId: string;
  eventType: AuditEventType;
  actorSub: string;
  actorEmail: string;
  actorRole: string;
  timestamp: string;
  oldValue?: string;
  newValue?: string;
}

export interface StaffProfile {
  sub: string;
  email: string;
  role: string;
  displayName: string;
}

export interface PublicProject {
  pid: string;
  projectReferenceNumber: string;
  displayName: string;
  mainAddress: string;
}

export async function resolveProject(projectReferenceNumber: string): Promise<PublicProject> {
  return (await raw('POST', '/public/projects/resolve', null, {
    projectReferenceNumber,
  })) as PublicProject;
}

export async function listPublicProjectAddresses(
  pid: string,
): Promise<{ project: Omit<PublicProject, 'projectReferenceNumber'>; addresses: PublicAddress[] }> {
  return (await raw(
    'GET',
    `/public/projects/${encodeURIComponent(pid)}/addresses`,
    null,
  )) as { project: Omit<PublicProject, 'projectReferenceNumber'>; addresses: PublicAddress[] };
}

export function makeApi(getToken: () => string | null) {
  const request = async <T>(method: string, path: string, body?: unknown): Promise<T> => {
    const token = getToken();
    if (!token) throw new ApiError(401, 'not signed in');
    return (await raw(method, path, token, body)) as T;
  };

  return {
    // ---- customer ----
    me: () => request<Me>('GET', '/me'),
    addMyAddress: (addressId: string) =>
      request<{ addressId: string; claimStatus: ClaimStatus; accountStatus: AccountStatus }>(
        'POST',
        '/me/addresses',
        { addressId },
      ),
    listMyIssues: () => request<{ issues: Issue[] }>('GET', '/me/issues'),
    createIssue: (input: {
      projectId: string;
      addressId: string;
      title: string;
      description: string;
      priority: IssuePriority;
    }) => request<Issue>('POST', '/me/issues', input),
    getMyIssue: (issueId: string) =>
      request<Issue>('GET', `/me/issues/${encodeURIComponent(issueId)}`),
    updateMyIssue: (
      issueId: string,
      input: Partial<Pick<Issue, 'title' | 'description' | 'status' | 'priority'>>,
    ) => request<Issue>('PATCH', `/me/issues/${encodeURIComponent(issueId)}`, input),
    requestAttachmentUpload: (
      issueId: string,
      input: { filename: string; contentType: string; size: number },
    ) =>
      request<{ attachment: Attachment; uploadUrl: string }>(
        'POST',
        `/me/issues/${encodeURIComponent(issueId)}/attachments`,
        input,
      ),
    getAttachmentDownload: (issueId: string, attachmentId: string) =>
      request<{ attachment: Attachment; downloadUrl: string }>(
        'GET',
        `/me/issues/${encodeURIComponent(issueId)}/attachments/${encodeURIComponent(
          attachmentId,
        )}/download`,
      ),
    listMyIssueComments: (issueId: string) =>
      request<{ comments: Comment[] }>('GET', `/me/issues/${encodeURIComponent(issueId)}/comments`),
    createMyIssueComment: (issueId: string, body: string) =>
      request<Comment>('POST', `/me/issues/${encodeURIComponent(issueId)}/comments`, { body }),
    updateMyIssueComment: (issueId: string, commentId: string, body: string) =>
      request<Comment>(
        'PATCH',
        `/me/issues/${encodeURIComponent(issueId)}/comments/${encodeURIComponent(commentId)}`,
        { body },
      ),
    deleteMyIssueComment: (issueId: string, commentId: string) =>
      request<{ deleted: boolean }>(
        'DELETE',
        `/me/issues/${encodeURIComponent(issueId)}/comments/${encodeURIComponent(commentId)}`,
      ),
    addCommentAttachment: (
      issueId: string,
      commentId: string,
      input: { filename: string; contentType: string; size: number },
    ) =>
      request<{ attachment: Attachment; uploadUrl: string }>(
        'POST',
        `/me/issues/${encodeURIComponent(issueId)}/comments/${encodeURIComponent(commentId)}/attachments`,
        input,
      ),
    getCommentAttachmentDownload: (issueId: string, commentId: string, attachmentId: string) =>
      request<{ attachment: Attachment; downloadUrl: string }>(
        'GET',
        `/me/issues/${encodeURIComponent(issueId)}/comments/${encodeURIComponent(commentId)}/attachments/${encodeURIComponent(attachmentId)}/download`,
      ),
    listIssueAuditEvents: (issueId: string) =>
      request<{ events: AuditEvent[] }>(
        'GET',
        `/me/issues/${encodeURIComponent(issueId)}/audit`,
      ),
    updateMyNotificationPreferences: (input: { emailOnStaffComment: boolean }) =>
      request<{ notificationPreferences: NotificationPreferences }>(
        'PATCH',
        '/me/notification-preferences',
        input,
      ),

    // ---- admin: customers ----
    adminListCustomers: (params: { accountStatus?: string; flagReason?: string; search?: string } = {}) => {
      const q = new URLSearchParams();
      if (params.accountStatus) q.set('accountStatus', params.accountStatus);
      if (params.flagReason) q.set('flagReason', params.flagReason);
      if (params.search) q.set('search', params.search);
      const qs = q.toString();
      return request<{ customers: AdminCustomerSummary[] }>(
        'GET',
        `/admin/customers${qs ? `?${qs}` : ''}`,
      );
    },
    adminGetCustomer: (sub: string) =>
      request<{
        meta: AdminCustomerSummary;
        addresses: (Omit<MeAddress, 'projects'> & { claimStatus: ClaimStatus })[];
        links: { projectId: string; addressId: string; linkedAt: string }[];
        projects: AdminProject[];
      }>('GET', `/admin/customers/${encodeURIComponent(sub)}`),
    adminUpgrade: (sub: string) =>
      request<{ sub: string; accountTier: AccountTier }>(
        'POST',
        `/admin/customers/${encodeURIComponent(sub)}/upgrade`,
      ),
    adminDowngrade: (sub: string) =>
      request<{ sub: string; accountTier: AccountTier }>(
        'POST',
        `/admin/customers/${encodeURIComponent(sub)}/downgrade`,
      ),
    adminDisable: (sub: string) =>
      request('POST', `/admin/customers/${encodeURIComponent(sub)}/disable`),
    adminEnable: (sub: string) =>
      request('POST', `/admin/customers/${encodeURIComponent(sub)}/enable`),
    adminDeregister: (sub: string) =>
      request<{ sub: string; deregistered: boolean; backupKey: string }>(
        'POST',
        `/admin/customers/${encodeURIComponent(sub)}/deregister`,
      ),
    adminFlag: (sub: string) =>
      request('POST', `/admin/customers/${encodeURIComponent(sub)}/flag`, {}),
    adminUnflag: (sub: string) =>
      request('POST', `/admin/customers/${encodeURIComponent(sub)}/unflag`, {}),
    adminPromoteClaim: (sub: string, addressId: string, force = false) =>
      request(
        'POST',
        `/admin/customers/${encodeURIComponent(sub)}/addresses/${encodeURIComponent(
          addressId,
        )}/promote`,
        { force },
      ),
    adminDeleteLink: (sub: string, projectId: string, addressId: string) =>
      request(
        'DELETE',
        `/admin/customers/${encodeURIComponent(sub)}/links/${encodeURIComponent(
          projectId,
        )}/${encodeURIComponent(addressId)}`,
      ),

    // ---- admin: addresses ----
    adminListAddresses: (params: { search?: string; projectId?: string } = {}) => {
      const q = new URLSearchParams();
      if (params.search) q.set('search', params.search);
      if (params.projectId) q.set('projectId', params.projectId);
      const qs = q.toString();
      return request<{ addresses: AdminAddress[] }>(
        'GET',
        `/admin/addresses${qs ? `?${qs}` : ''}`,
      );
    },
    adminCreateAddress: (input: {
      streetAddress: string;
      suburb: string;
      state: string;
      postcode: string;
    }) => request<AdminAddress>('POST', '/admin/addresses', input),
    adminPatchAddress: (
      addressId: string,
      input: Partial<Pick<AdminAddress, 'streetAddress' | 'suburb' | 'state' | 'postcode'>>,
    ) => request<AdminAddress>('PATCH', `/admin/addresses/${encodeURIComponent(addressId)}`, input),
    adminAttachAddress: (addressId: string, pid: string) =>
      request(
        'POST',
        `/admin/addresses/${encodeURIComponent(addressId)}/projects/${encodeURIComponent(pid)}`,
        {},
      ),
    adminDetachAddress: (addressId: string, pid: string) =>
      request(
        'DELETE',
        `/admin/addresses/${encodeURIComponent(addressId)}/projects/${encodeURIComponent(pid)}`,
      ),

    // ---- admin: projects ----
    adminListProjects: () => request<{ projects: AdminProject[] }>('GET', '/admin/projects'),
    adminGetProject: (pid: string) =>
      request<{ project: AdminProject; addressCount: number }>(
        'GET',
        `/admin/projects/${encodeURIComponent(pid)}`,
      ),
    adminCreateProject: (input: {
      projectReferenceNumber: string;
      displayName: string;
      mainAddress: string;
    }) => request<AdminProject>('POST', '/admin/projects', input),
    adminPatchProject: (
      pid: string,
      input: Partial<Pick<AdminProject, 'displayName' | 'mainAddress'>>,
    ) => request<AdminProject>('PATCH', `/admin/projects/${encodeURIComponent(pid)}`, input),
    adminListProjectAddresses: (pid: string) =>
      request<{ addresses: AdminAddress[] }>(
        'GET',
        `/admin/projects/${encodeURIComponent(pid)}/addresses`,
      ),
    adminBulkGenerate: (
      pid: string,
      input: {
        streetAddressTemplate: string;
        unitRange: string;
        suburb: string;
        state: string;
        postcode: string;
        reuseExistingAddresses?: boolean;
      },
    ) =>
      request<{ created: number; reused: number; total: number }>(
        'POST',
        `/admin/projects/${encodeURIComponent(pid)}/bulk-generate-addresses`,
        input,
      ),

    // ---- admin: issues ----
    adminCreateIssue: (input: {
      customerSub: string;
      projectId: string;
      addressId: string;
      title: string;
      description: string;
      priority: IssuePriority;
    }) => request<Issue>('POST', '/admin/issues', input),
    adminListIssues: (params: {
      projectId?: string;
      addressId?: string;
      customerSub?: string;
      status?: IssueStatus;
      priority?: IssuePriority;
    } = {}) => {
      const q = new URLSearchParams();
      for (const [k, v] of Object.entries(params)) if (v) q.set(k, v);
      const qs = q.toString();
      return request<{ issues: Issue[] }>('GET', `/admin/issues${qs ? `?${qs}` : ''}`);
    },
    adminPatchIssue: (
      issueId: string,
      input: {
        projectId: string;
        addressId: string;
        title?: string;
        description?: string;
        status?: IssueStatus;
        priority?: IssuePriority;
      },
    ) => request<Issue>('PATCH', `/admin/issues/${encodeURIComponent(issueId)}`, input),
    // adminDeleteIssue removed 2026-04-28 — issues are immutable from the
    // admin UI; use status: 'resolved' to take an issue out of the active
    // queue. If destruction is genuinely needed, run a one-off DDB script.
    adminRequestAttachmentUpload: (
      issueId: string,
      input: { projectId: string; addressId: string; filename: string; contentType: string; size: number },
    ) =>
      request<{ attachment: Attachment; uploadUrl: string }>(
        'POST',
        `/admin/issues/${encodeURIComponent(issueId)}/attachments`,
        input,
      ),
    adminGetAttachmentDownload: (
      issueId: string,
      attachmentId: string,
      projectId: string,
      addressId: string,
    ) =>
      request<{ attachment: Attachment; downloadUrl: string }>(
        'GET',
        `/admin/issues/${encodeURIComponent(issueId)}/attachments/${encodeURIComponent(attachmentId)}/download?projectId=${encodeURIComponent(projectId)}&addressId=${encodeURIComponent(addressId)}`,
      ),
    adminListIssueComments: (issueId: string, projectId: string, addressId: string) =>
      request<{ comments: Comment[] }>(
        'GET',
        `/admin/issues/${encodeURIComponent(issueId)}/comments?projectId=${encodeURIComponent(projectId)}&addressId=${encodeURIComponent(addressId)}`,
      ),
    adminCreateIssueComment: (issueId: string, projectId: string, addressId: string, body: string) =>
      request<Comment>(
        'POST',
        `/admin/issues/${encodeURIComponent(issueId)}/comments`,
        { projectId, addressId, body },
      ),
    adminUpdateIssueComment: (
      issueId: string,
      commentId: string,
      projectId: string,
      addressId: string,
      body: string,
    ) =>
      request<Comment>(
        'PATCH',
        `/admin/issues/${encodeURIComponent(issueId)}/comments/${encodeURIComponent(commentId)}`,
        { projectId, addressId, body },
      ),
    adminDeleteIssueComment: (
      issueId: string,
      commentId: string,
      projectId: string,
      addressId: string,
    ) =>
      request<{ deleted: boolean }>(
        'DELETE',
        `/admin/issues/${encodeURIComponent(issueId)}/comments/${encodeURIComponent(commentId)}?projectId=${encodeURIComponent(projectId)}&addressId=${encodeURIComponent(addressId)}`,
      ),
    adminAddCommentAttachment: (
      issueId: string,
      commentId: string,
      projectId: string,
      addressId: string,
      input: { filename: string; contentType: string; size: number },
    ) =>
      request<{ attachment: Attachment; uploadUrl: string }>(
        'POST',
        `/admin/issues/${encodeURIComponent(issueId)}/comments/${encodeURIComponent(commentId)}/attachments`,
        { projectId, addressId, ...input },
      ),
    adminGetCommentAttachmentDownload: (
      issueId: string,
      commentId: string,
      attachmentId: string,
      projectId: string,
      addressId: string,
    ) =>
      request<{ attachment: Attachment; downloadUrl: string }>(
        'GET',
        `/admin/issues/${encodeURIComponent(issueId)}/comments/${encodeURIComponent(commentId)}/attachments/${encodeURIComponent(attachmentId)}/download?projectId=${encodeURIComponent(projectId)}&addressId=${encodeURIComponent(addressId)}`,
      ),
    adminListIssueAuditEvents: (issueId: string, projectId: string, addressId: string) =>
      request<{ events: AuditEvent[] }>(
        'GET',
        `/admin/issues/${encodeURIComponent(issueId)}/audit?projectId=${encodeURIComponent(projectId)}&addressId=${encodeURIComponent(addressId)}`,
      ),

    // Staff profile
    getAdminProfile: () => request<StaffProfile>('GET', '/admin/me'),
    patchAdminProfile: (displayName: string) =>
      request<{ displayName: string }>('PATCH', '/admin/me', { displayName }),
  };
}

export type PortalApi = ReturnType<typeof makeApi>;
